/**
 * Results API Layer - Collection
 * Initialises Tagalys Collection SDK for collection templates
 */

class ResultsAPICollection {
  constructor () {
    this.eventBus = window.theme.tagalys.results.eventBus
    this.catalogPage = 1
    this.catalogLastPageCount = 0

    const start = () => {
      if (!this.collectionId) {
        this.initializeShopifyCatalog()
        return
      }

      // eslint-disable-next-line no-undef
      onTagalysReady(() => {
        if (!window.Tagalys?.APIConnector?.Collection) {
          console.error('Tagalys APIConnector.Collection is not available')
          return
        }
        this.initializeTagalysCollection()
      })
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start)
    } else {
      start()
    }
  }

  extractSearchParams () {
    const urlParams = new URLSearchParams(window.location.search)
    const filters = {}
    const filterParams = urlParams.getAll('filter')

    filterParams.forEach(filterParam => {
      const filterSplit = filterParam.split('~')
      filterSplit.forEach(param => {
        const dashIndex = param.indexOf('-')
        if (dashIndex === -1) return

        const filterName = param.substring(0, dashIndex)
        let values = param.substring(dashIndex + 1).split(',')
        const canSplitDash = values[0].split('-')

        if (canSplitDash.length === 2) {
          values = {
            selected_min: parseInt(canSplitDash[0], 10),
            selected_max: parseInt(canSplitDash[1], 10),
          }
        }

        filters[filterName] = values
      })
    })

    const page = parseInt(urlParams.get('page'), 10) || 1
    const sortOrder = urlParams.get('sort') || urlParams.get('sort_by') || ''

    return {
      filters,
      sortOrder,
      page,
    }
  }

  initializeTagalysCollection () {
    const { filters, sortOrder, page } = this.extractSearchParams()
    const container = this.container
    const hasFilters = Object.keys(filters).length > 0
    // Shopify's automatic "all" catalogue is often absent from Tagalys merchandising.
    const useShopifyOnly = this.collectionHandle === 'all'

    if (container) {
      container.setAttribute('aria-busy', 'true')
    }

    window.theme.tagalys.results.SDK = window.Tagalys.APIConnector.Collection.new(
      {
        params: {
          collectionId: this.collectionId,
          filters: {
            ...filters,
          },
          sort: sortOrder,
          perPage: this.productsPerPage,
          page: page || 1,
        },
        forceFailover: useShopifyOnly,
        failover: this.initializeShopifyCollection.bind(this),
        onSuccess: response => {
          if (!useShopifyOnly && !hasFilters && !response?.products?.length) {
            this.failoverToShopify()
            return
          }

          if (container) {
            container.setAttribute('aria-busy', 'false')
          }
          this.eventBus.publish('results:api:fetched:success', response)
        },
        onFailure: (response, helpers) => {
          if (container) {
            container.setAttribute('aria-busy', 'false')
          }
          console.error('Collection fetch failed', response)
        },
      },
    )
    window.theme.tagalys.results.SDK.helpers.call()
  }

  failoverToShopify () {
    const sdk = this.initializeShopifyCollection()
    window.theme.tagalys.results.SDK = sdk
    sdk?.helpers?.call()
  }

  initializeShopifyCollection () {
    return window.Tagalys.APIConnector.ShopifyCollection.new({
      params: {
        collectionId: this.collectionId,
        perPage: this.productsPerPage,
      },
      onSuccess: (response, helpers) => {
        if (this.container) {
          this.container.setAttribute('aria-busy', 'false')
        }
        this.eventBus.publish('results:api:fetched:success', response)
      },
      onFailure: (response, helpers) => {
        if (this.container) {
          this.container.setAttribute('aria-busy', 'false')
        }
        console.error(
          'Collection fetch failed for both Tagalys and Shopify',
          response,
        )
      },
    })
  }

  initializeShopifyCatalog () {
    const { page } = this.extractSearchParams()
    this.catalogPage = page || 1
    this.bindCatalogSdk()
    this.fetchShopifyCatalogPage(this.catalogPage)
  }

  bindCatalogSdk () {
    window.theme.tagalys.results.SDK = {
      helpers: {
        call: () => this.fetchShopifyCatalogPage(this.catalogPage),
        getRequestParams: () => ({
          page: this.catalogPage,
        }),
        getEncodedQueryString: params => {
          const page = params?.page || this.catalogPage
          return page > 1 ? `page=${page}` : ''
        },
        getAppliedFilters: () => [],
        getSortOptions: () => [],
        applyFilter: () => {},
        clearFilter: () => {},
        clearAllFilters: () => {},
        applySortOption: () => {},
        goToPage: nextPage => {
          this.fetchShopifyCatalogPage(nextPage)
        },
        hasNextPage: () => {
          const total = this.catalogTotalCount
          if (total) {
            return this.catalogPage * this.productsPerPage < total
          }
          return this.catalogLastPageCount === this.productsPerPage
        },
        hasPreviousPage: () => this.catalogPage > 1,
      },
    }
  }

  async fetchShopifyCatalogPage (page) {
    const handle = this.collectionHandle || 'all'
    const perPage = this.productsPerPage
    const container = this.container

    if (container) {
      container.setAttribute('aria-busy', 'true')
    }

    const root = window.routes?.rootUrl || '/'
    const prefix = root.endsWith('/') ? root : `${root}/`
    const url = `${prefix}collections/${handle}/products.json?limit=${perPage}&page=${page}`

    try {
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`Catalog fetch failed (${response.status})`)
      }

      const data = await response.json()
      const products = (data.products || []).map(product => ({
        id: product.id,
        handle: product.handle,
      }))

      this.catalogPage = page
      this.catalogLastPageCount = products.length

      if (container) {
        container.setAttribute('aria-busy', 'false')
      }

      this.eventBus.publish('results:api:fetched:success', {
        products,
        page,
        per_page: perPage,
        total_count: this.catalogTotalCount || products.length,
        filters: [],
        sort_options: [],
      })
    } catch (error) {
      if (container) {
        container.setAttribute('aria-busy', 'false')
      }
      console.error('Collection fetch failed for Shopify catalogue', error)
    }
  }

  get collectionRoot () {
    return document.querySelector('[data-collection-id]')
  }

  get collectionId () {
    return this.collectionRoot?.dataset.collectionId
  }

  get collectionHandle () {
    return this.collectionRoot?.dataset.collectionHandle || ''
  }

  get catalogTotalCount () {
    return Number(this.collectionRoot?.dataset.productsCount) || 0
  }

  get container () {
    return document.querySelector(
      '[data-collection-filters-form-external-el="container"]',
    )
  }

  get productsPerPage () {
    const fromConfig = Number(window.theme.resultsConfig?.productsPerPage)
    const fromDom = Number(this.collectionRoot?.dataset.productsPerPage)
    return fromDom || fromConfig || 24
  }
}

window.theme.tagalys.results.apiLayer = new ResultsAPICollection()
